import { FormControl } from '@ngneat/reactive-forms';

import {
  Accordion,
  AddressRaw,
  BreadcrumbItem,
} from '@digital-platform/rs-cms/domain';
import { SectionedDropdownItem } from '../models/dropdown.interface';
import { PageMetaData } from '../models/page-meta-data.interface';
import { provinceList, stateList } from './address-constants';

export enum ContactTypes {
  LANDING = '',
  RESIDENTIAL = 'residential',
  BUSINESS = 'business',
  ENVIRONMENTAL = 'environmental-solutions',
}

const enum AccordionItemTypes {
  FAQ = 'faqItem',
}

export const BreadCrumbsData: { [key in ContactTypes]: BreadcrumbItem[] } = {
  [ContactTypes.LANDING]: [
    {
      type: 'breadcrumbItem',
      status: true,
      linkTitle: 'Contact Us',
      linkUrl: '/contact-us',
    },
  ],
  [ContactTypes.RESIDENTIAL]: [
    {
      type: 'breadcrumbItem',
      status: true,
      linkTitle: 'Contact Us',
      linkUrl: '/contact-us',
    },
    {
      type: 'breadcrumbItem',
      status: true,
      linkTitle: 'Residential',
      linkUrl: '/contact-us/residential',
    },
  ],
  [ContactTypes.BUSINESS]: [
    {
      type: 'breadcrumbItem',
      status: true,
      linkTitle: 'Contact Us',
      linkUrl: '/contact-us',
    },
    {
      type: 'breadcrumbItem',
      status: true,
      linkTitle: 'Business',
      linkUrl: '/contact-us/business',
    },
  ],
  [ContactTypes.ENVIRONMENTAL]: [
    {
      type: 'breadcrumbItem',
      status: true,
      linkTitle: 'Contact Us',
      linkUrl: '/contact-us',
    },
    {
      type: 'breadcrumbItem',
      status: true,
      linkTitle: 'Environmental Solutions',
      linkUrl: '/contact-us/environmental-solutions',
    },
  ],
};

export const FaqsData = {
  [ContactTypes.RESIDENTIAL]: [
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title: 'Will my pickup schedule change during the holidays?',
      body: 'Holiday schedules vary by service location. Residential collection may be delayed due to these and other holidays: Memorial Day, July 4, Labor Day, Thanksgiving Day, Christmas Day and New Year\'s Day. As a general rule, holidays push our residential pickup schedules back one day with regular schedules resuming the following week. Commercial collection holiday schedules vary by location. To find out if your schedule is impacted by potential service interruptions, please <a href="../account/login">login</a> to your account and click on "Pickup Schedule" from within our account management portal. If you do not have an online account you can also view your holiday <a href="../schedule">schedule</a>.',
    },
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title: 'What is my pickup schedule?',
      body: 'To view your holiday schedule click on "Pickup Schedule" from the Account Dashboard or visit our <a href="../schedule">schedule</a> page.',
    },
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title: 'Do you provide bulk pickup?',
      body: 'Visit our <a href="../residents/bulk-waste">Bulk Service</a> page or contact your local customer service representative to find if your address qualifies for bulk waste pickup.',
    },
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title: 'How do I report a missed pickup?',
      body: 'To report a problem and request a resolution online, you must first <a href="../account/signup">create a free account</a>, login to your account, and complete the steps on our <a href="../account/services/missed-pickup">missed pickup page</a>. A customer resource representative will contact you promptly. To call us, please enter your address above for your local customer service number.',
    },
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title: 'How can I pay my bill online?',
      body: 'You can pay your bill, view invoices and set up automatic payments simply by <a href="../account/signup">registering online</a>, and linking your existing Republic Services account.',
    },
  ],
  [ContactTypes.BUSINESS]: [
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title: 'Will my pickup schedule change during the holidays?',
      body: 'Holiday schedules vary by service location. Commercial collection holiday schedules vary by location. To find out if your schedule is impacted by potential service interruptions, please <a href="../account/login">login</a> to your account and click on "Pickup Schedule" from within our account management portal. If you do not have an online account you can also view your holiday <a href="../schedule">schedule</a>.',
    },
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title: 'What is my pickup schedule?',
      body: 'To view your holiday schedule click on "Pickup Schedule" from the Account Dashboard or visit our <a href="../schedule">schedule</a> page.',
    },
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title: 'How do I report a missed pickup?',
      body: 'To report a problem and request a resolution online, you must first <a href="../account/signup">create a free account</a>, login to your account, and complete the steps on our <a href="../account/services/missed-pickup">missed pickup page</a>. A customer resource representative will contact you promptly. To call us, please enter your address above for your local customer service number.',
    },
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title: 'How can I pay my bill online?',
      body: 'You can pay your bill, view invoices and set up automatic payments simply by <a href="../account/signup">registering online</a>, and linking your existing Republic Services account.',
    },
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title: 'How do I report a damaged cart or container?',
      body: 'To report a problem and request a resolution online, <a href="../account/login">login to your account</a> and complete a request. A customer resource representative will contact you promptly. To call us, find your local office phone number by entering your service address above.',
    },
  ],
  [ContactTypes.ENVIRONMENTAL]: [
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title: 'What is Environmental Solutions?',
      body:
        'Republic Services Environmental Solutions offers customers environmental services across four categories:<br />' +
        '<ul><li>Treatment & Disposal</li>' +
        '<li>Field & Industrial Services</li>' +
        '<li>24/7 Emergency Response</li>' +
        '<li>E&P Services</li> </ul>' +
        "Environmental Solutions offers industrial, commercial and government customers a comprehensive suite of solutions to securely transport, treat, dispose, and recycle hazardous and non-hazardous waste. Republic Services' acquisitions of US Ecology, ACV Enviro and Ecoflo make us an industry leader with over 70 years of experience providing safe and compliant solutions for regulated waste with RCRA Subtitle C and D landfills, a nationwide network of TSDFs, service centers and recycling facilities. Our focus on safety, environmental compliance, and best-in-class customer service enables us to effectively meet the needs of customers. We effectively manage the inherent environmental risks with a commitment to continuous improvement of processes and operations.",
    },
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title: 'How do I request Emergency Response service? ',
      body:
        ' 24/7 Emergency Response <a href="tel:800.899.4672">(800) 899-4672</a><br />' +
        ' <br />' +
        'Republic Services offers unmatched capabilities, key resources and expertise from performing 10,000+ responses every year. We are ready to support you with any type of emergency from small scale spills to natural disasters and catastrophic events. Our 24/7 emergency response hotline is ready to respond to your next emergency with nationwide coverage from hundreds of response locations throughout North America. We are also the leading commercial OSRO (NRC) and industry leader for high hazard response (SRS).',
    },
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title: 'Does Republic Services provide treatment and disposal services?',
      body:
        `Republic Services provides safe and compliant solutions for complex waste management needs and ensures the effective management of the environmental risks our customers face. We have a coast-to-coast network of TSDFs positioned throughout North America. They are located near industrial centers in the West, Northeast, Midwest and Gulf with a broad range of permits and acceptance criteria, infrastructure to support high volume transfer with truck and rail access. <a href="../environmental-solutions/treatment-disposal">Learn more</a>. <br />` +
        ' <br />' +
        '<ul><li>Five Haz / Non-Haz Landfills (Co-located with treatment capabilities) </li>' +
        '<li>Ability to manage a wide range of liquid and solid waste streams </li>' +
        '<li>Haz Inorganic Stabilization </li>' +
        '<li>Organic Chemical Oxidation </li>' +
        '<li>Corrosive Neutralization </li>' +
        '<li>Oxidizer Deactivation </li>' +
        '<li>Micro / Macro encapsulation </li>' +
        '<li>Organic / Inorganic WWT </li>' +
        '<li>Broad range of de-characterization and de-listing capabilities </li>' +
        '<li>State-of-the-art air handling </li>' +
        '<li>Three E&P landfills in Texas to support drilling waste generated by the oil and gas industry </li>' +
        '<li>Radioactive Waste Landfill (Class A, B, C)</li> ',
    },
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title:
        'Does Republic Services provide industrial cleaning and maintenance services? ',
      body: `Our experienced workforce can provide turnkey services utilizing the most current technologies for industrial cleaning and maintenance, vacuum transfer and pumping, facility decommissioning and closure, turn around and shutdown work, tank cleaning, processing and maintenance, decontamination and demolition, asbestos/lead/PCB/mold abatement and disposal, tank and equipment rental and more. <a href="../environmental-solutions/field-services/industrial-cleaning">Learn more.</a>`,
    },
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title: 'Who do I contact to schedule a service or pick up? ',
      body:
        "Providing our customers a best-in-class customer experience is fundamental to our mission of offering the industry's premier environmental solutions and industrial services. Our team of highly trained customer service professionals is dedicated to this goal. We pride ourselves in providing responsive and knowledgeable service to support our customers' needs by engineering innovative solutions that you can trust. To schedule a service or pick-up, contact your Customer Service Representative or, for General Customer Service inquiries, call <a href='tel:800.592.5489'>(800) 592-5489</a>.<br />" +
        ' ',
    },
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title:
        'Does Republic Services have an online portal for my waste profiles and reporting? ',
      body:
        'Yes. COR (Customer Online Resource) is the industry\'s leading online platform that allows you to efficiently manage your industrial services and environmental waste needs from any device. Create a login using your Customer ID# found in the Welcome email you received from your Customer Service Representative and start using it today! To learn more about COR <a href="../account/login">click here</a>. <br />' +
        ' ',
    },
    {
      type: AccordionItemTypes.FAQ,
      status: true,
      title: 'How do I complete a waste profile form? ',
      body: 'We encourage you to use our industry-leading online platform, Customer Online Resource (COR), which allows you to easily complete a profile from any device. <a href="../account/login">Click here</a> to create a login using your Customer ID# or contact Customer Service at <a href="tel:800.592.5489">(800) 592-5489</a> for assistance.  ',
    },
  ],
};

export const CardTextData = [
  {
    contactType: ContactTypes.RESIDENTIAL,
    cardTitle: 'Residential Services',
    faqsHeader: 'Top FAQs for Residential',
    contactHeadline: 'Customer Service',
    contactText: 'Chat with us',
    additionalText: [
      {
        classes: ['d-inline-block', 'fw-bold'],
        type: 'text',
        text: 'Monday - Friday: ',
      },
      {
        classes: ['d-inline-block'],
        type: 'text',
        text: '7:30AM to 5:00PM for all time zones (Call Wed - Fri for shorter wait times)',
      },
      {
        classes: ['d-inline-block', 'fw-bold'],
        type: 'text',
        text: 'Saturday: ',
      },
      {
        classes: ['d-inline-block'],
        type: 'link',
        text: 'Login/create an account for self-service options',
        route: '/account/login',
      },
    ],
    body: 'Reliable recycling and waste services, dumpster rental and more. ',
    altText: 'containers on curb',
    imgSrc: '/sites/default/files/styles/three_cards_image/public/2.jpg.webp',
  },
  {
    contactType: ContactTypes.BUSINESS,
    cardTitle: 'Business Services',
    faqsHeader: 'Top FAQs for Business',
    contactHeadline: 'Customer Service',
    contactText: 'Chat with us',
    additionalText: [
      {
        classes: ['d-inline-block', 'fw-bold'],
        type: 'text',
        text: 'Monday - Friday: ',
      },
      {
        classes: ['d-inline-block'],
        type: 'text',
        text: '7:30AM to 5:00PM for all time zones (Call Wed - Fri for shorter wait times)',
      },
      {
        classes: ['d-inline-block', 'fw-bold'],
        type: 'text',
        text: 'Saturday: ',
      },
      {
        classes: ['d-inline-block'],
        type: 'link',
        text: 'Login/create an account for self-service options',
        route: '/account/login',
      },
    ],
    body: 'Customized recycling, waste services and products that fit your business.',
    altText: 'recycling and garbage container delivery',
    imgSrc: '/sites/default/files/styles/three_cards_image/public/3_0.jpg.webp',
  },
  {
    contactType: ContactTypes.ENVIRONMENTAL,
    cardTitle: 'Environmental Solutions',
    faqsHeader: 'Top FAQs for Environmental Solutions',
    contactHeadline: 'Customer Service',
    contactFormHeader: 'How can we help? Send us a message!',
    contactText: 'Contact us',
    additionalText: [
      { classes: [], type: 'text', text: '24/7 Emergency Response Hotline' },
    ],
    subAdditionalText: 'Give us a call',
    body: 'Treatment and disposal, field and industrial services, 24/7 ER and E&P services.',
    altText: 'environmental solutions worker',
    imgSrc: '/sites/default/files/styles/three_cards_image/public/4_0.jpg.webp',
  },
];

export interface FaqsType {
  [key: string]: Accordion['accordionItems'];
}

interface AdditionalTextItem {
  classes: string[];
  type: string;
  text: string;
  route?: string;
}

export interface CardType {
  contactType: ContactTypes;
  cardTitle: string;
  faqsHeader: string;
  contactHeadline: string;
  contactText: string;
  additionalText: AdditionalTextItem[];
  subAdditionalText?: string;
  contactFormHeader?: string;
  body: string;
  altText: string;
  imgSrc: string;
}

export const industryOptions = [
  {
    text: 'Automotive',
    value: '',
  },
  {
    text: 'Construction and Demolition',
    value: '',
  },
  {
    text: 'Education',
    value: '',
  },
  {
    text: 'Energy and Utilities',
    value: '',
  },
  {
    text: 'Engineering and Consulting',
    value: '',
  },

  {
    text: 'Government',
    value: '',
  },
  {
    text: 'Healthcare and Pharmacology',
    value: '',
  },
  {
    text: 'Manufacturing',
    value: '',
  },
  {
    text: 'Oil, Gas and Petrochemical',
    value: '',
  },
  {
    text: 'Retail and Hospitality',
    value: '',
  },
  {
    text: 'Steel and Metal Finishing',
    value: '',
  },
  {
    text: 'Transportation',
    value: '',
  },
  {
    text: 'Other',
    value: '',
  },
];

export const inquiryTypeOptions = [
  {
    text: 'Waste Disposal, Recycling, or Transportation',
    value: '1',
  },
  {
    text: 'Lab Pack',
    value: '2',
  },
  {
    text: 'Emergency, Disaster, or Spill Response',
    value: '3',
  },
  {
    text: 'Industrial Cleaning',
    value: '4',
  },
  {
    text: 'Remediation',
    value: '5',
  },
  {
    text: 'Other',
    value: '6',
  },
];

/* Unused, for now */
export const stateProvinceOptions: SectionedDropdownItem[] = [
  { title: 'States', list: stateList },
  {
    title: 'Provinces/Territories',
    list: provinceList,
  },
];

export interface PardotInquiryForm {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  [key: string]: any;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  businessName: FormControl<string>;
  industry: FormControl<string>;
  industryOther: FormControl<string>;
  email: FormControl<string>;
  phoneNumber: FormControl<string>;
  inquiryType: FormControl<string>;
  inquiryTypeOther: FormControl<string>;
  zipCode: FormControl<string>;
  currentCustomer: FormControl<boolean>;
  message: FormControl<string>;
  utmCampaign?: FormControl<string>;
  utmMedium?: FormControl<string>;
  utmContent?: FormControl<string>;
  utmSource?: FormControl<string>;
  utmTerm?: FormControl<string>;
}

export interface AddressLookupForm {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  [key: string]: any;
  customerType: FormControl<string | null>;
  address: FormControl<AddressRaw | string>;
  businessType: FormControl<string>;
}

export const esEmergencyResponseNumber = '800.899.4672';

export const metaInfo: { [key: string]: PageMetaData } = {
  [ContactTypes.LANDING]: {
    title: 'Contact Us Republic Services',
    description:
      'Visit our Contact Us page to find information on services, explore support topics and find our contact information.',
  },
  [ContactTypes.RESIDENTIAL]: {
    title: 'Contact Us Residential Customers',
    description:
      'Visit our Residential Contact Us page to find information on services, explore support topics and find our contact information.',
  },
  [ContactTypes.BUSINESS]: {
    title: 'Contact Us Commercial Customers',
    description:
      'Visit our Contact Us page for commercial customers to find information on services, explore support topics and find our contact information.',
  },
  [ContactTypes.ENVIRONMENTAL]: {
    title: 'Contact Us Environmental Solutions Customers',
    description:
      'Visit our Contact Us page for Environmental Solutions customers to find information on Environmental Solutions services offered, explore support topics and find our contact information.',
  },
};
